import type { SupportedPlatform } from './constants';
import type { SupportedLocale } from './locale';

const BLABLACAR_SUPPORTED_LOCALES = ['fr-FR', 'es-ES'] as const;

export type BlablacarLocale = (typeof BLABLACAR_SUPPORTED_LOCALES)[number];

const BFF_STATIC_IMG_ADVERTISING_BASE_URL = 'https://bff.viamichelin.com/static/img/advertising';
const DISTANCE_MINIMUM_BOXMAP_BLABLACAR_IN_METERS = 80_000;
const PRICE_BY_KILOMETER = 0.135;
const DEFAULT_TRIP_PRICE = 25;

const BLABLACAR_BOXMAP_URLS: Record<BlablacarLocale, Record<SupportedPlatform, string>> = {
	'fr-FR': {
		WEB_DESKTOP:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRDESKTOP',
		WEB_MOBILE:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRMOBILE',
		WEB_TABLET:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRDESKTOP',
		ANDROID:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRAPP',
		ANDROID_TABLET:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRAPP',
		IOS:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRAPP',
		IPAD:
			'https://www.blablacar.fr/offer-seats?comuto_cmkt=FR_VIAMICHELIN_ACQ_DRVR_BONUSCOVOITURAGE_BOXFDRAPP',
	},
	'es-ES': {
		WEB_DESKTOP:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
		WEB_MOBILE:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
		WEB_TABLET:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
		ANDROID:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
		ANDROID_TABLET:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
		IOS:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
		IPAD:
			'https://www.blablacar.es/offer-seats?from={{departureName}}&to={{arrivalName}}&comuto_cmkt=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR&utm_source=VIAMICHELIN&utm_medium=Link&utm_campaign=ES_VIAMICHELIN_DRVR_NEWSITE_SUDR',
	},
};

const BLABLACAR_BOXMAP_TRACKING_URLS: Record<BlablacarLocale, Record<SupportedPlatform, string>> = {
	'fr-FR': {
		WEB_DESKTOP:
			'https://pubads.g.doubleclick.net/gampad/clk?id=4590738479&iu=/3513/via_int_1_1x1_resultiti/comuto_pixel_desk_fr&cachebuster={{timestamp}}',
		WEB_MOBILE:
			'https://pubads.g.doubleclick.net/gampad/clk?id=4590581035&iu=/7734/TrackingPixel/comuto/webapp/fr/mitg.1x1.1&cachebuster={{timestamp}}',
		WEB_TABLET:
			'https://pubads.g.doubleclick.net/gampad/clk?id=4590362539&iu=/3513/via_int_1t_1x1_resultiti/comuto_pixel_tab_fr&cachebuster={{timestamp}}',
		ANDROID: '',
		ANDROID_TABLET: '',
		IOS: '',
		IPAD: '',
	},
	'es-ES': {
		WEB_DESKTOP:
			'https://pubads.g.doubleclick.net/gampad/clk?id=4590857753&iu=/3513/via_int_1_1x1_resultiti/comuto_pixel_desk_es&cachebuster={{timestamp}}',
		WEB_MOBILE:
			'https://pubads.g.doubleclick.net/gampad/clk?id=4590970550&iu=/7734/TrackingPixel/comuto/webapp/es/mitg.1x1.1&cachebuster={{timestamp}}',
		WEB_TABLET:
			'https://pubads.g.doubleclick.net/gampad/clk?id=4590857756&iu=/3513/via_int_1t_1x1_resultiti/comuto_pixel_tab_es&cachebuster={{timestamp}}',
		ANDROID: '',
		ANDROID_TABLET: '',
		IOS: '',
		IPAD: '',
	},
};

export function isBlablacarSupportedLocale(locale: SupportedLocale): locale is BlablacarLocale {
	return BLABLACAR_SUPPORTED_LOCALES.includes(locale as BlablacarLocale);
}

export function getBlablacarBoxMapData(locale: SupportedLocale, platform: SupportedPlatform, distanceInMeters?: number) {
	const showBlablacarBoxmap = isBlablacarSupportedLocale(locale) && distanceInMeters && distanceInMeters >= DISTANCE_MINIMUM_BOXMAP_BLABLACAR_IN_METERS;
	if (!showBlablacarBoxmap) {
		return;
	}

	const mapLanguageToBlablacarBoxMapMessage: Record<BlablacarLocale, string> = {
		'fr-FR': 'Covoiturez et économisez !',
		'es-ES': `Ahorra hasta ${calculateBlablacarSavingsFromDistance(distanceInMeters)}€`,
	};

	return {
		img: `${BFF_STATIC_IMG_ADVERTISING_BASE_URL}/blablacar/boxmap/brand-logo_w160.webp`,
		message: mapLanguageToBlablacarBoxMapMessage[locale],
		url: BLABLACAR_BOXMAP_URLS[locale][platform],
		tracking: BLABLACAR_BOXMAP_TRACKING_URLS[locale][platform],
		shortMessage: '',
	};
}

/**
 * Calculates savings amount in euros from distance in meters
 * @param distanceInMeters - Distance in meters
 * @returns Savings amount in euros
 */
export function calculateBlablacarSavingsFromDistance(distanceInMeters: number): number {
	return distanceInMeters ? Math.round((distanceInMeters / 1000) * PRICE_BY_KILOMETER) : DEFAULT_TRIP_PRICE;
}
